import React, { useEffect, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import * as THREE from 'three';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import TWEEN from '@tweenjs/tween.js';
import * as turf from '@turf/turf';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.REACT_APP_BACKEND_URL;

countries.registerLocale(enLocale);

const LoginPage = ({ onLogin, userNotAllowed }) => {
    const globeEl = useRef();
    const [polygons, setPolygons] = useState([]);
    const [labelsData, setLabelsData] = useState([]);

    function getCountryCentroid(feature) {
        try {
            const centroid = turf.centroid(feature);
            const [lng, lat] = centroid.geometry.coordinates;
            return { lat, lng };
        } catch (e) {
            console.warn("Centroid calculation failed for", feature.id, e);
            return null;
        }
    }

    useEffect(() => {
        Promise.all([
            fetch('/data/world.geojson').then(res => res.json()),
            fetch('/data/countries.json').then(res => res.json())
        ]).then(([geoData, countryData]) => {
            const visitedSet = new Set(countryData.map(c => c.code));
            const coloredPolygons = geoData.features.map(f => ({
                ...f,
                color: visitedSet.has(f.id) ? '#DBB6A6' : undefined
            }));
            setPolygons(coloredPolygons);

            const labels = geoData.features
                .filter(f => countryData.find(c => c.code === f.id))
                .map(f => {
                    const c = countryData.find(c => c.code === f.id);
                    //console.log("c: " + JSON.stringify(c, null, 2))
                    const centroid = getCountryCentroid(f);
                    if (!centroid || isNaN(centroid.lat) || isNaN(centroid.lng)) return null;
                    return {
                        lat: centroid.lat + (Math.random() - 0.5) * 0.05,
                        lng: centroid.lng + (Math.random() - 0.5) * 0.05,
                        label: `${c.count}`,
                        size: Math.max(0.5, Math.min(2, c.count / 5)),
                        code: f.id
                    };
                }).filter(Boolean);
            //console.log("labels", labels);
            setLabelsData(labels);
        });
    }, []);

    useEffect(() => {
        if (globeEl.current) {
            globeEl.current.controls().autoRotate = true;
            globeEl.current.controls().autoRotateSpeed = 0.3;

            // Set initial camera view to Europe
            globeEl.current.pointOfView({ lat: 50, lng: 60, altitude: 1.5 }, 0); // latitude/longitude around central Europe
            //globeEl.current.pointOfView({ lat: 50, lng: 10, altitude: 1.5 }, 0); // latitude/longitude around central Europe

        }
    }, []);


    const handleLogin = () => {
        window.location.href = `${BACKEND_URL}/auth/google`;
    };

    return (
        <div style={{ position: 'relative', height: '100vh', width: '100vw' }}>
            <Globe
                ref={globeEl}
                globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                backgroundColor="rgba(0,0,0,1)"
                polygonsData={polygons}
                polygonCapColor={d => d.color || 'rgba(255,255,255,0.05)'}
                polygonSideColor={() => 'rgba(128, 128, 128, 0.15)'}
                polygonStrokeColor={d => "#808080" || '#111'}
                labelLat={d => d.lat}
                labelLng={d => d.lng}
                labelText={d => d.label}
                labelSize={d => d.size}
                labelDotRadius={0.4}
                labelResolution={2}
                htmlElementsData={labelsData}
                htmlLat={d => d.lat}
                htmlLng={d => d.lng}
                htmlAltitude={() => 0.002}
                htmlElement={d => {
                    //console.log(d);
                    const iso2 = countries.alpha3ToAlpha2(d.code || '');
                    const el = document.createElement('div');
                    el.style.zIndex = '10';
                    //console.log("iso2: " + iso2)
                    el.className = 'country-count-badge';
                    el.style.backgroundImage = `url("https://flagcdn.com/w40/${iso2.toLowerCase()}.png")`;

                    const textSpan = document.createElement('span');
                    if (parseInt(d.label) === 999) {
                        textSpan.innerHTML = `
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="white" viewBox="0 0 448 512">
                              <path d="M438.6 105.4c12.5 12.5 12.5 32.8 
                              0 45.3l-240 240c-12.5 12.5-32.8 12.5-45.3 
                              0l-120-120c-12.5-12.5-12.5-32.8 
                              0-45.3s32.8-12.5 45.3 0L192 306.7 
                              393.4 105.4c12.5-12.5 32.8-12.5 
                              45.3 0z"/>
                            </svg>
                        `;
                        textSpan.title = "Lived here";
                    } else {
                        textSpan.textContent = d.label;
                    }
                    textSpan.className = 'badge-text';
                    el.appendChild(textSpan);
                    return el;
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.4)',
                }}
            >
                <div
                    style={{
                        padding: '24px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        maxWidth: '300px',
                        width: '90%',
                    }}
                >
                    {userNotAllowed && (
                        <div
                            style={{
                                marginBottom: '16px',
                                color: '#ffdddd',
                                backgroundColor: '#660000',
                                padding: '12px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}
                        >
                            You are not allowed to log in. Contact <a href="mailto:hei@artiom.no" style={{ color: '#fff' }}>hei@artiom.no</a> to get access.
                        </div>
                    )}
                    <h2 style={{
                        marginBottom: '16px',
                        fontSize: '20px',
                        lineHeight: '1.3',
                        color: '#fff',
                        position: 'relative',
                        zIndex: 2000
                    }}>
                        See the world. Mark your story
                    </h2>
                    <button
                        style={{
                            position: 'relative',
                            padding: '12px 20px',
                            fontSize: '15px',
                            background: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(6px)',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            width: '100%',
                            transition: 'background 0.3s',
                            zIndex: 1000
                        }}
                        onClick={handleLogin}
                    >
                        Sign in with Google
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;